/**
 * HousingStaffDashboard - Main dashboard for housing staff after login
 * Shows menu options based on permissions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, BedDouble, Users, CalendarCheck, User, LogOut,
  ClipboardList, Key, Sparkles, BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HousingStaffDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('dashboard');
  const [stats, setStats] = useState(null);

  const token = getToken();
  // Get permissions - check housing staff first, then fall back to admin (admin has all permissions)
  const housingPermissions = JSON.parse(localStorage.getItem('housing_staff_permissions') || '[]');
  const adminRole = localStorage.getItem('admin_role');
  const isAdmin = !!adminRole || !!getToken();
  // Admins get full access
  const permissions = housingPermissions.length > 0 ? housingPermissions : (isAdmin ? ['*'] : []);
  const staffName = localStorage.getItem('housing_staff_name') || localStorage.getItem('admin_name') || 'Staff';
  const staffRole = localStorage.getItem('housing_staff_role') || adminRole || 'staff';

  // Check permissions
  const canViewRooms = permissions.includes('housing.view_rooms') || permissions.includes('*');
  const canManageRooms = permissions.includes('housing.manage_rooms') || permissions.includes('*');
  const canViewBookings = permissions.includes('housing.view_bookings') || permissions.includes('*');
  const canManageBookings = permissions.includes('housing.manage_bookings') || permissions.includes('*');
  const canCheckIn = permissions.includes('housing.check_in') || permissions.includes('*');
  const canCheckOut = permissions.includes('housing.check_out') || permissions.includes('*');
  const canUpdateRoomStatus = permissions.includes('housing.update_room_status') || permissions.includes('*');
  const canManageGuests = permissions.includes('housing.manage_guests') || permissions.includes('*');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/accommodation/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          setStats(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('eventflow_token');
    localStorage.removeItem('token');
    localStorage.removeItem('housing_staff_token');
    localStorage.removeItem('housing_staff_role');
    localStorage.removeItem('housing_staff_name');
    localStorage.removeItem('housing_staff_permissions');
    toast.success('Logged out successfully');
    navigate('/housing/login');
  };

  const roleNames = {
    'housing_manager': 'Housing Manager',
    'front_desk': 'Front Desk',
    'housekeeping': 'Housekeeping',
    'viewer': 'Viewer'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50" data-testid="housing-staff-dashboard">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Housing Portal</h1>
                <p className="text-sm text-gray-500">Welcome back, {staffName}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-indigo-100 text-indigo-800">
                {roleNames[staffRole] || staffRole}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => setActiveView('profile')}>
                <User className="h-4 w-4 mr-2" />
                Profile
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeView === 'profile' ? (
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                My Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{staffName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <Badge className="bg-indigo-100 text-indigo-800">
                  {roleNames[staffRole] || staffRole}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {permissions.map((perm, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {perm.split('.')[1] || perm}
                    </Badge>
                  ))}
                </div>
              </div>
              <Button className="w-full mt-4" onClick={() => setActiveView('dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Quick Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Rooms</p>
                        <p className="text-3xl font-bold text-indigo-600">{stats.total_rooms || 0}</p>
                      </div>
                      <BedDouble className="h-8 w-8 text-indigo-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Ready</p>
                        <p className="text-3xl font-bold text-green-600">{stats.ready_rooms || 0}</p>
                      </div>
                      <Sparkles className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Occupied</p>
                        <p className="text-3xl font-bold text-blue-600">{stats.occupied_rooms || 0}</p>
                      </div>
                      <Key className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Occupancy</p>
                        <p className="text-3xl font-bold text-purple-600">{stats.occupancy_rate || 0}%</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Today's Activity */}
            {stats && (
              <div className="grid grid-cols-2 gap-4 mb-8">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <CalendarCheck className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-green-700">Expected Arrivals</p>
                        <p className="text-2xl font-bold text-green-800">{stats.expected_arrivals || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <LogOut className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-orange-700">Expected Departures</p>
                        <p className="text-2xl font-bold text-orange-800">{stats.expected_departures || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Menu Options */}
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Check-in */}
              {canCheckIn && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-green-300"
                  onClick={() => navigate('/admin/accommodation')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-2">
                      <Key className="h-8 w-8 text-green-600" />
                    </div>
                    <CardTitle>Check-In</CardTitle>
                    <CardDescription>Process guest check-ins and assign rooms</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-green-500 hover:bg-green-600">
                      Quick Check-In
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Room Status */}
              {(canViewRooms || canUpdateRoomStatus) && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-blue-300"
                  onClick={() => navigate('/admin/accommodation')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-2">
                      <BedDouble className="h-8 w-8 text-blue-600" />
                    </div>
                    <CardTitle>Room Status</CardTitle>
                    <CardDescription>
                      {canUpdateRoomStatus ? 'View and update room status' : 'View room availability'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      View Rooms
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Bookings */}
              {canViewBookings && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-purple-300"
                  onClick={() => navigate('/admin/accommodation')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-2">
                      <ClipboardList className="h-8 w-8 text-purple-600" />
                    </div>
                    <CardTitle>Bookings</CardTitle>
                    <CardDescription>View and manage room bookings</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      View Bookings
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Guests */}
              {canManageGuests && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-indigo-300"
                  onClick={() => navigate('/admin/accommodation')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-2">
                      <Users className="h-8 w-8 text-indigo-600" />
                    </div>
                    <CardTitle>Guests</CardTitle>
                    <CardDescription>Manage guest information and records</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      View Guests
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Housekeeping - Update Room Status */}
              {canUpdateRoomStatus && (
                <Card 
                  className="cursor-pointer hover:shadow-lg transition-all hover:scale-102 border-2 border-transparent hover:border-yellow-300"
                  onClick={() => navigate('/admin/accommodation')}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-2">
                      <Sparkles className="h-8 w-8 text-yellow-600" />
                    </div>
                    <CardTitle>Housekeeping</CardTitle>
                    <CardDescription>Update room cleaning status</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full">
                      Update Status
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default HousingStaffDashboard;
